import React, { useState } from "react";
import { Pivot, PivotItem, TooltipHost, DirectionalHint, Icon } from "@fluentui/react";
import { ChatMessage as ChatMessageType } from "../../../../interfaces/copilot-chat.interfaces";
import MarkdownWithImages from "../../../../pages/MarkdownWithImages";
import FunctionStepCard from "./FunctionStepCard";
import MessageFeedbackDialog, { MessageFeedbackData } from "./MessageFeedbackDialog";
import { friendlySource, timelineThoughtsText } from "../../../../Utils/copilotTimeline";

// A single block of intermediate agent reasoning, shown ChatGPT/Copilot style:
// a small dimmed header naming the phase, with the reasoning rendered as markdown.
const ThoughtBubble: React.FC<{ source?: string; text: string }> = ({ source, text }) => (
    <div style={{ marginBottom: 8, padding: "8px 12px", background: "#faf9f8", borderRadius: 8, borderLeft: "3px solid #c8c6c4", color: "#605e5c" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, color: "#8a8886", fontSize: 12, fontWeight: 600 }}>
            <Icon iconName="Lightbulb" style={{ fontSize: 13 }} />
            {friendlySource(source)}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            <MarkdownWithImages text={text} images={[]} />
        </div>
    </div>
);


interface ChatMessageProps {
    message: ChatMessageType;
    messageIndex: number;
    expandedStates: { [key: string]: boolean };
    onToggleExpand: (key: string) => void;
    onFeedback?: (messageId: string | undefined, action: boolean | null, feedbackData?: MessageFeedbackData) => void; // action: true=like, false=dislike, null=toggle off
    onShareChat?: () => void;
    isSubmittingFeedback?: boolean;
    onRetry?: () => void;
}

// Action button styles
const actionButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "#605e5c",
    padding: 0,
};

const actionButtonHoverStyle: React.CSSProperties = {
    backgroundColor: "#e1dfdd",
};

const actionButtonActiveStyle: React.CSSProperties = {
    backgroundColor: "#0078d4",
    color: "#ffffff",
};

const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    messageIndex,
    expandedStates,
    onToggleExpand,
    onFeedback,
    onShareChat,
    isSubmittingFeedback = false,
    onRetry,
}) => {
    const [copied, setCopied] = useState(false);
    // Initialize liked state from message.action (from API response)
    const [liked, setLiked] = useState<boolean | null>(message.action ?? null);
    const [hoveredButton, setHoveredButton] = useState<string | null>(null);
    const [attachmentExpanded, setAttachmentExpanded] = useState(false);
    // Feedback dialog state
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
    const [pendingReactionType, setPendingReactionType] = useState<boolean | null>(null);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(message.text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const handleLike = () => {
        // If already liked, toggle off without showing dialog
        if (liked === true) {
            setLiked(null);
            if (onFeedback) {
                onFeedback(message.id, null);
            }
        } else {
            // Show feedback dialog for new like action
            setPendingReactionType(true);
            setIsFeedbackDialogOpen(true);
        }
    };

    const handleDislike = () => {
        // If already disliked, toggle off without showing dialog
        if (liked === false) {
            setLiked(null);
            if (onFeedback) {
                onFeedback(message.id, null);
            }
        } else {
            // Show feedback dialog for new dislike action
            setPendingReactionType(false);
            setIsFeedbackDialogOpen(true);
        }
    };

    const handleFeedbackSubmit = async (feedbackData: MessageFeedbackData) => {
        setLiked(feedbackData.action);
        if (onFeedback) {
            onFeedback(message.id, feedbackData.action, feedbackData);
        }
        setIsFeedbackDialogOpen(false);
        // Delay resetting pendingReactionType to avoid flash of wrong header during close animation
        setTimeout(() => setPendingReactionType(null), 300);
    };

    const handleFeedbackDismiss = () => {
        setIsFeedbackDialogOpen(false);
        // Delay resetting pendingReactionType to avoid flash of wrong header during close animation
        setTimeout(() => setPendingReactionType(null), 300);
    };

    const handleShare = () => {
        // Use the onShareChat prop to share the entire chat session
        if (onShareChat) {
            onShareChat();
        }
    };

    const getButtonStyle = (buttonId: string, isActive: boolean = false): React.CSSProperties => ({
        ...actionButtonStyle,
        ...(hoveredButton === buttonId && !isActive ? actionButtonHoverStyle : {}),
        ...(isActive ? actionButtonActiveStyle : {}),
    });

    return (
        <div
            style={{
                display: "flex",
                justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                marginBottom: "12px",
            }}
        >
            <div
                style={{
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    borderRadius: "18px",
                    maxWidth: "80%",
                    backgroundColor: message.role === "user" ? "#0078D7" : (message.isError ? "#fff4f4" : "#f8f9fa"),
                    color: message.role === "user" ? "#fff" : "#000",
                    fontSize: "14px",
                    boxShadow: message.role === "user" ? "none" : (message.isError ? "0 2px 8px rgba(196,49,75,0.12)" : "0 2px 8px rgba(0,0,0,0.08)"),
                    border: message.isError ? "1px solid #f1bfc8" : undefined,
                }}
            >
                {/* Inline error message with retry button */}
                {message.isError ? (
                    <div style={{ padding: "12px 8px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                iconName="ErrorBadge"
                                style={{ fontSize: "18px", color: "#c4314b", flexShrink: 0, marginTop: "2px" }}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: "#c4314b", fontSize: "13px", marginBottom: "4px" }}>
                                    Something went wrong
                                </div>
                                <div style={{ color: "#605e5c", fontSize: "13px", lineHeight: "1.5" }}>
                                    We couldn&apos;t get a response. This may be a temporary issue.
                                    You can retry and it should work.
                                </div>
                            </div>
                        </div>
                        {onRetry && (
                            <button
                                onClick={onRetry}
                                style={{
                                    marginTop: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "7px 14px",
                                    background: "#c4314b",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "background 0.2s ease",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#a02840" }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "#c4314b" }}
                                aria-label="Retry sending the message"
                            >
                                <Icon iconName="Refresh" style={{ fontSize: "14px" }} />
                                Retry
                            </button>
                        )}
                    </div>
                ) : message.role === "ai" ? (
                    <>
                        {message.isStreaming ? (
                            <div style={{ padding: "8px 4px" }}>
                                {message.timeline && message.timeline.length > 0 && (
                                    <div style={{ marginBottom: 10 }}>
                                        {message.timeline.map((item, tIndex) => {
                                            if (item.kind === "thought") {
                                                return <ThoughtBubble key={item.id} source={item.source} text={item.text} />;
                                            }
                                            const fnKey = `${messageIndex}-tl-${item.step.callId || tIndex}`;
                                            return (
                                                <FunctionStepCard
                                                    key={fnKey}
                                                    functionCall={item.step}
                                                    index={tIndex}
                                                    isExpanded={expandedStates[fnKey] ?? false}
                                                    onToggleExpand={() => onToggleExpand(fnKey)}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                                <div style={{ fontSize: "14px" }}>
                                    {message.text ? (
                                        <MarkdownWithImages text={message.text} images={message.images || []} />
                                    ) : (
                                        (!message.timeline || message.timeline.length === 0) && (
                                            <span style={{ color: "#605e5c", fontStyle: "italic" }}>Working…</span>
                                        )
                                    )}
                                </div>
                            </div>
                        ) : (
                        <Pivot aria-label="Agent Response Tabs">
                            {/* ANSWER TAB */}
                            <PivotItem headerText="Answer" itemIcon="Message">
                                <div style={{ padding: "8px 4px", fontSize: "14px" }}>
                                    {(() => {
                                        const thoughtsText = timelineThoughtsText(message.timeline) || message.thoughts;
                                        return thoughtsText ? (
                                            <details style={{ marginBottom: 8 }}>
                                                <summary style={{ cursor: "pointer", color: "#8a8886", fontSize: 12 }}>Thoughts</summary>
                                                <div style={{ padding: "6px 10px", color: "#605e5c", fontSize: 12, whiteSpace: "pre-wrap" }}>{thoughtsText}</div>
                                            </details>
                                        ) : null;
                                    })()}
                                    <MarkdownWithImages text={message.text} images={message.images || []} />
                                </div>
                            </PivotItem>

                            {/* STEPS TAB */}
                            <PivotItem headerText="Steps" itemIcon="Flow">
                                <div style={{ marginTop: "6px" }}>
                                    {message.functionCalls?.length === 0 || !message.functionCalls ? (
                                        <div style={{ color: "#666", fontSize: "14px" }}>
                                            No function steps available.
                                        </div>
                                    ) : (
                                        message.functionCalls.map((fn, index) => {
                                            const fnKey = `${messageIndex}-${index}`;
                                            const expanded = expandedStates[fnKey] ?? index === 0;

                                            return (
                                                <FunctionStepCard
                                                    key={fnKey}
                                                    functionCall={fn}
                                                    index={index}
                                                    isExpanded={expanded}
                                                    onToggleExpand={() => onToggleExpand(fnKey)}
                                                />
                                            );
                                        })
                                    )}
                                </div>
                            </PivotItem>
                        </Pivot>
                        )}

                        {/* Action Buttons for Agent Messages */}
                        {!message.isStreaming && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "8px 4px",
                                borderTop: "1px solid #e0e0e0",
                                marginTop: "8px",
                            }}
                        >
                            {/* Copy Button */}
                            <TooltipHost
                                content={copied ? "Copied!" : "Copy response"}
                                directionalHint={DirectionalHint.topCenter}
                            >
                                <button
                                    style={getButtonStyle("copy", copied)}
                                    onClick={handleCopy}
                                    onMouseEnter={() => setHoveredButton("copy")}
                                    onMouseLeave={() => setHoveredButton(null)}
                                    aria-label="Copy response"
                                >
                                    <Icon iconName={copied ? "CheckMark" : "Copy"} style={{ fontSize: 16 }} />
                                </button>
                            </TooltipHost>

                            {/* Like Button */}
                            <TooltipHost
                                content={liked === true ? "Liked" : "Like this response"}
                                directionalHint={DirectionalHint.topCenter}
                            >
                                <button
                                    style={getButtonStyle("like", liked === true)}
                                    onClick={handleLike}
                                    onMouseEnter={() => setHoveredButton("like")}
                                    onMouseLeave={() => setHoveredButton(null)}
                                    aria-label="Like response"
                                >
                                    <Icon iconName={liked === true ? "LikeSolid" : "Like"} style={{ fontSize: 16 }} />
                                </button>
                            </TooltipHost>

                            {/* Dislike Button */}
                            <TooltipHost
                                content={liked === false ? "Disliked" : "Dislike this response"}
                                directionalHint={DirectionalHint.topCenter}
                            >
                                <button
                                    style={getButtonStyle("dislike", liked === false)}
                                    onClick={handleDislike}
                                    onMouseEnter={() => setHoveredButton("dislike")}
                                    onMouseLeave={() => setHoveredButton(null)}
                                    aria-label="Dislike response"
                                >
                                    <Icon iconName={liked === false ? "DislikeSolid" : "Dislike"} style={{ fontSize: 16 }} />
                                </button>
                            </TooltipHost>

                            {/* Share Button */}
                            <TooltipHost
                                content="Share chat"
                                directionalHint={DirectionalHint.topCenter}
                            >
                                <button
                                    style={getButtonStyle("share")}
                                    onClick={handleShare}
                                    onMouseEnter={() => setHoveredButton("share")}
                                    onMouseLeave={() => setHoveredButton(null)}
                                    aria-label="Share chat"
                                >
                                    <Icon iconName="Share" style={{ fontSize: 16 }} />
                                </button>
                            </TooltipHost>

                            {/* Spacer to push AI Generated label to the right */}
                            <div style={{ flex: 1 }} />

                            {/* AI Generated Label */}
                            <TooltipHost
                                content={
                                    <div style={{ padding: "8px 4px", maxWidth: "280px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                                            <Icon iconName="Info" style={{ fontSize: 14, color: "#0078d4" }} />
                                            <span style={{ fontWeight: 600, fontSize: "13px", color: "#323130" }}>AI-Generated Content</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: "12px", lineHeight: "1.5", color: "#605e5c" }}>
                                            This response is generated by an AI system and may be inaccurate or incomplete. Please verify the information independently before relying on it.
                                        </p>
                                    </div>
                                }
                                directionalHint={DirectionalHint.topCenter}
                                calloutProps={{
                                    styles: {
                                        beak: { background: "#ffffff" },
                                        beakCurtain: { background: "#ffffff" },
                                        calloutMain: {
                                            background: "#ffffff",
                                            borderRadius: "8px",
                                            boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
                                            border: "1px solid #e1dfdd"
                                        }
                                    }
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        padding: "5px 10px",
                                        borderRadius: "16px",
                                        background: "#f5f5f5",
                                        border: "1px solid #e1dfdd",
                                        cursor: "help",
                                        transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = "#c8c6c4";
                                        e.currentTarget.style.background = "#e1dfdd";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "#e1dfdd";
                                        e.currentTarget.style.background = "#f5f5f5";
                                    }}
                                >
                                    <Icon
                                        iconName="Sparkle"
                                        style={{
                                            fontSize: 12,
                                            color: "#605e5c"
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            color: "#605e5c",
                                            userSelect: "none"
                                        }}
                                    >
                                        AI Generated
                                    </span>
                                </div>
                            </TooltipHost>
                        </div>
                        )}
                    </>
                ) : (
                    // User message rendering with attachment support
                    <div style={{ padding: "8px 4px" }}>
                        <MarkdownWithImages text={message.text} images={message.images || []} />

                        {/* Attachment indicator and expandable content */}
                        {message.hasAttachment && message.attachmentData &&
                            message.attachmentData.groundingInstructions &&
                            message.attachmentData.description && message.attachmentData.description.trim() !== "" && (
                                <div style={{ marginTop: "8px", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "8px" }}>
                                    <button
                                        onClick={() => setAttachmentExpanded(!attachmentExpanded)}
                                        style={{
                                            background: "rgba(255,255,255,0.1)",
                                            border: "1px solid rgba(255,255,255,0.2)",
                                            borderRadius: "12px",
                                            padding: "4px 8px",
                                            color: "#fff",
                                            fontSize: "11px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            transition: "all 0.2s ease"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                                        }}
                                    >
                                        <Icon iconName="Attach" style={{ fontSize: "10px" }} />
                                        <span>Datasets attached</span>
                                        <Icon iconName={attachmentExpanded ? "ChevronUp" : "ChevronDown"} style={{ fontSize: "10px" }} />
                                    </button>

                                    {attachmentExpanded && (
                                        <div style={{
                                            marginTop: "8px",
                                            padding: "8px",
                                            background: "rgba(255,255,255,0.1)",
                                            borderRadius: "8px",
                                            fontSize: "11px",
                                            border: "1px solid rgba(255,255,255,0.2)"
                                        }}>
                                            {message.attachmentData.description && (
                                                <div style={{ color: "rgba(255,255,255,0.8)", wordWrap: "break-word", whiteSpace: "pre-line" }}>
                                                    {message.attachmentData.description}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                    </div>
                )}
            </div>

            {/* Message Feedback Dialog */}
            <MessageFeedbackDialog
                isOpen={isFeedbackDialogOpen}
                reactionType={pendingReactionType}
                onDismiss={handleFeedbackDismiss}
                onSubmit={handleFeedbackSubmit}
                isSubmitting={isSubmittingFeedback}
            />
        </div>
    );
};

export default ChatMessage;
